import {
  Button,
  Col,
  DatePicker,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  getCompanies,
  getTrainings,
} from "../repository/data.repository";
import { Training } from "../repository/data.default";
import { QueryOperation } from "../code/search-query/search-query";
import { createSearchRequest } from "../hooks/useSearchRequest";
import RemoteSelect from "../components/analytics/RemoteSelect";
import { getProblemMessage } from "../utils/http";

// PD-01 §10 — Training Reports. Lists every training (optionally filtered by
// company + date range) with its status + feedback + completion marker. Click
// a row to inspect the host's full feedback write-up.
function TrainingReportsPage() {
  const [companyUid, setCompanyUid] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [items, setItems] = useState<Training[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Training | null>(null);

  const companiesRequest = createSearchRequest(getCompanies, "name");

  useEffect(() => {
    setLoading(true);
    const conditions: any[] = [];
    if (companyUid) {
      conditions.push({
        field: "companyUid",
        values: [companyUid],
        operation: QueryOperation.Equal,
      });
    }
    if (range && range[0] && range[1]) {
      conditions.push({
        field: "startDate",
        values: [range[0].startOf("day").toISOString(), range[1].endOf("day").toISOString()],
        operation: QueryOperation.Between,
      });
    }
    getTrainings({
      searchQuery: { conditions },
      pageNumber: 0,
      pageSize: 500,
      sortBy: "startDate",
      sortDescending: true,
    })
      .then(({ data }) => setItems(data))
      .catch((err) =>
        message.error(getProblemMessage(err) || "Failed to load trainings."),
      )
      .finally(() => setLoading(false));
  }, [companyUid, range]);

  const columns: ColumnsType<Training> = [
    { title: "Title", dataIndex: "title", ellipsis: true, fixed: "left" },
    { title: "Company", dataIndex: "companyName", width: 180 },
    { title: "Host", dataIndex: "hostUserName", width: 180 },
    {
      title: "Start",
      dataIndex: "startDate",
      width: 140,
      render: (v: string) => dayjs(v).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (status: string) => {
        const color =
          status === "Completed"
            ? "green"
            : status === "Cancelled"
              ? "red"
              : "blue";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Completed At",
      dataIndex: "completedAt",
      width: 160,
      render: (v?: string) =>
        v ? dayjs(v).format("DD/MM/YYYY HH:mm") : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: "Feedback",
      dataIndex: "feedback",
      render: (v?: string | null) =>
        v ? (
          <Typography.Text ellipsis style={{ maxWidth: 320 }}>
            {v}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ];

  const totals = {
    total: items.length,
    completed: items.filter((t) => t.status === "Completed").length,
    cancelled: items.filter((t) => t.status === "Cancelled").length,
    withFeedback: items.filter((t) => (t.feedback ?? "").trim().length > 0).length,
  };

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Training Reports
        </Typography.Title>
      </Row>
      <Col className="flex flex-auto flex-col gap-4 overflow-hidden p-4 pt-0">
        <Row align="middle" gutter={12}>
          <Col>
            <Typography.Text type="secondary" className="mr-2">
              Company
            </Typography.Text>
            <RemoteSelect
              className="w-64"
              placeholder="All companies"
              value={companyUid}
              onChange={setCompanyUid}
              request={companiesRequest}
            />
          </Col>
          <Col>
            <Typography.Text type="secondary" className="mr-2">
              Period
            </Typography.Text>
            <DatePicker.RangePicker
              value={range}
              onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
              allowClear
            />
          </Col>
        </Row>

        <Space>
          <Tag>Total: {totals.total}</Tag>
          <Tag color="green">Completed: {totals.completed}</Tag>
          <Tag color="red">Cancelled: {totals.cancelled}</Tag>
          <Tag color="blue">With feedback: {totals.withFeedback}</Tag>
        </Space>

        <Table
          rowKey="uid"
          size="small"
          loading={loading}
          dataSource={items}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          scroll={{ x: 1200 }}
          onRow={(row) => ({
            onClick: () => setSelected(row),
            style: { cursor: "pointer" },
          })}
        />
      </Col>

      <Modal
        open={!!selected}
        title={selected?.title}
        footer={<Button onClick={() => setSelected(null)}>Close</Button>}
        onCancel={() => setSelected(null)}
        width={640}
      >
        {selected && (
          <Space direction="vertical" className="w-full">
            <Row gutter={8}>
              <Col flex="auto"><strong>Status:</strong> {selected.status}</Col>
              <Col><strong>Company:</strong> {selected.companyName ?? "—"}</Col>
            </Row>
            <Row gutter={8}>
              <Col flex="auto"><strong>Host:</strong> {selected.hostUserName ?? "—"}</Col>
              <Col>
                <strong>When:</strong> {dayjs(selected.startDate).format("DD MMM YYYY HH:mm")}
                {" → "}
                {dayjs(selected.endDate).format("HH:mm")}
              </Col>
            </Row>
            {selected.completedAt && (
              <Row>
                <Col flex="auto">
                  <strong>Completed:</strong>{" "}
                  {dayjs(selected.completedAt).format("DD MMM YYYY HH:mm")}
                </Col>
              </Row>
            )}
            <div className="mt-3">
              <Typography.Text strong>Host feedback</Typography.Text>
              <div
                className="mt-1 rounded p-3"
                style={{ background: "rgba(0,0,0,0.03)", whiteSpace: "pre-wrap" }}
              >
                {selected.feedback ?? "No feedback captured yet."}
              </div>
            </div>
          </Space>
        )}
      </Modal>
    </Col>
  );
}

export default TrainingReportsPage;
